/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Nikolay Sviridov
 * @version 1.33.0
 * @since 1.33.0
 */

import { useEffect, useRef, useState } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import { useDropzone } from 'react-dropzone';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { ELinkTarget } from '@/main/components/common/ExternalLink/ExternalLink';

import { icons } from '../../../assets';
import { Button, InfoBlock, LinkBlock } from '../../../components/common';
import { ImgStyled } from '../../../components/common/Block/Img';
import { EColorStatus, EFileExtension, MapExtensionToIcon } from '../../../modules/app/appConstants';
import EFormValidation from '../../../modules/form/const/formValidation';
import { ELocaleKeys } from '../../../modules/internationalization/intConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardImportSubscribersLimits,
} from '../../../modules/internationalization/intSelectors';
import { ICommonTranslation, IWizardImportSubscribersLimitsTranslation } from '../../../modules/internationalization/types';
import { IRootReducer } from '../../../reducers/rootReducer';
import { getFileExtension } from '../../../utils/formatText';
import { stringFormatParams } from '../../../utils/stringFormat';
import { IWizardImportSubscribersLimitsAttachFileActionPayload, IWizardImportSubscribersLimitsSetStepValidPayload } from '../types/wizardImportSubscribersLimitsActionTypes';
import { wizardImportSubscribersLimitsAttachFile, wizardImportSubscribersLimitsSetStepValidAction } from '../wizardImportSubscribersLimitsActions';
import { warningSettingsDefaultConst } from '../wizardImportSubscribersLimitsConstant';
import { IWizardImportSubscribersLimitsReducer } from '../wizardImportSubscribersLimitsReducer';
import {
    makeSelectWizardImportSubscribersLimitsAttachFileErrorCode,
    makeSelectWizardImportSubscribersLimitsFileName,
    makeSelectWizardImportSubscribersLimitsFileTemplateUrl,
    makeSelectWizardImportSubscribersLimitsImportFileFormat,
    makeSelectWizardImportSubscribersLimitsFileIsLoading,
    makeSelectWizardImportSubscribersLimitsMaxFileName,
    makeSelectWizardImportSubscribersLimitsMaxFileSize,
    makeSelectWizardImportSubscribersLimitsMaxImportSubscriberAmount,
    makeSelectWizardImportSubscribersLimitsWarningSettings,
} from '../wizardImportSubscribersLimitsSelectors';
import { WizardImportSubscribersLimitsStyled } from './styled/WizardImportSubscribersLimitsStyled';

const WizardImportSubscribersLimitsStepSelection = ({
                                            attachFile,
                                            setStepValid,
                                            commonTranslation,
                                            errorCode,
                                            fileIsLoading,
                                            fileTemplateUrl,
                                            importFileFormat,
                                            maxFileName,
                                            maxFileSize,
                                            maxImportSubscriberAmount,
                                            fileName,
                                            warningSettings,
                                            wizardTranslation,
                                        }: IConnectedProps & IDispatchProps) => {

    const { LimitsDocumentSelectionStep: tStep } = wizardTranslation;

    const [fileValidationError, setFileValidation] = useState<string | undefined>();
    const [readMore, setReadMore] = useState(false);
    const [showReadMore, setShowReadMore] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const maxNumberWarningTextSymbols = 
        warningSettings?.maxNumberWarningTextSymbols || warningSettingsDefaultConst.maxNumberWarningTextSymbols;
    const maxWarningRows = warningSettings?.maxWarningRows || warningSettingsDefaultConst.maxWarningRows;
    const defaultWarningRows = warningSettings?.defaultWarningRows || warningSettingsDefaultConst.defaultWarningRows;

    const warningText = tStep.warning.slice(0, maxNumberWarningTextSymbols - 1);

    useEffect(() => {
        setFileValidation(errorCode);
    }, [errorCode]);

    useEffect(() => {
        const clientHeight = ref?.current?.clientHeight ?? 0;
        const scrollHeight = ref?.current?.scrollHeight ?? 0;
        if (clientHeight < scrollHeight) {
            setShowReadMore(true);
        }
    },[tStep])

    const toggleReadMore = () => {
        setReadMore((val) => !val);
    }


    const onDropHandler = (acceptedFiles: File[]) => {

        const errorStrings = [];

        const checkFormat = importFileFormat.find((format) => {
            const fileFormatRegex = new RegExp('^.+\\.'+format+'$','gm');
            return acceptedFiles[0].name.match(fileFormatRegex);
        });

        if (importFileFormat.length > 0 && !checkFormat) {
            errorStrings.push(stringFormatParams(
                commonTranslation.form.validation[EFormValidation.fileFormatIncorrect], 
                { format: importFileFormat.join(', ') }));
        }

        if (maxFileName > 0 && acceptedFiles[0].name.length > maxFileName ) {
            errorStrings.push(stringFormatParams(
                commonTranslation.form.validation[EFormValidation.fileNameTooLong], 
                { nameLimit: maxFileName }));
        }

        if (maxFileSize > 0 && acceptedFiles[0].size > maxFileSize) {
            errorStrings.push(commonTranslation.form.validation[EFormValidation.fileSizeExceeded]);
        }

        if (errorStrings.length) {
            const errorString = errorStrings.join('. ');

            setFileValidation(errorString);
            setStepValid({ isStepValid: false, validateError: errorString });
        } else {
            setStepValid({ isStepValid: true, validateError: '' });
            setFileValidation(undefined);
            attachFile({ file: acceptedFiles[0] });
        }
    };

    const { getInputProps, open } = useDropzone({
        onDrop: onDropHandler,
        multiple: false,
        noClick: false,
        noKeyboard: false,
    });

    return (
        <WizardImportSubscribersLimitsStyled>
            <div style={{ marginBottom: 24 }}>
                <Typography
                    isDangerousInnerHTML
                    className="wizard-import-subscribers-limits__text_warning"
                    ref={ref}
                    sx={{
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: readMore ? maxWarningRows : defaultWarningRows,
                    }}
                >
                    {warningText}
                </Typography>
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
                    {showReadMore && <LinkBlock
                        onClick={toggleReadMore}
                    >
                        {readMore ? tStep.readLess : tStep.readMore}
                    </LinkBlock>}
                </div>
            </div>
            <div className="wizard-import-subscribers-limits__text_bold">
                {tStep.title}
            </div>
            <div>
                {tStep.template}&nbsp;
                {
                    fileTemplateUrl
                        ? <LinkBlock
                            isExternalUrl
                            dataName="import-personal-data-file-template-url"
                            target={ELinkTarget.BLANK}
                            url={fileTemplateUrl}
                        >
                            {tStep.templateUrl}
                        </LinkBlock>
                        : tStep.templateUrl
                }
            </div>
            <div style={{ whiteSpace: 'pre-line' }}>
                {stringFormatParams(tStep.maxImportSubs, {
                    [ELocaleKeys.number]: maxImportSubscriberAmount,
                })}
            </div>
            <div style={{ margin: '12px 0', maxWidth: 700 }}>
                <InfoBlock colorStatus={EColorStatus.WARNING}>
                    <Typography isDangerousInnerHTML>
                        {tStep.info}
                    </Typography>
                </InfoBlock>
            </div>
            {
                fileIsLoading && <ImgStyled className="medium" src={icons.searchIndicator} style={{ width: 32 }}/>
            }
            {
                !fileValidationError && fileName && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <ImgStyled
                            className="medium"
                            src={MapExtensionToIcon[getFileExtension(fileName) as EFileExtension]
                            ?? icons.defaultFileIcon}
                        />
                        <div>{fileName}</div>
                    </div>
                )
            }
            {
                fileValidationError && (
                    <div
                        className="wizard-import-subscribers-limits__text_error"
                        data-name="wizard-import-subscribers-limits-file-error"
                    >
                        {
                            stringFormatParams(fileValidationError, {
                                maxSubs: maxImportSubscriberAmount,
                            })
                            || commonTranslation.form.validation[EFormValidation.fileNotFound]
                        }
                    </div>
                )
            }
            <div style={{ marginTop: 12 }}>
                <Button
                    dataName="wizard-import-subscribers-limits-choose-file-btn"
                    kind="medium"
                    onClick={open}
                >
                    {commonTranslation.form.attachFile}
                </Button>
            </div>
            {/* Hidden input */}
            <input
                {...getInputProps()}
                data-name="wizard-import-subscribers-limits-choose-file-input"
            />
        </WizardImportSubscribersLimitsStyled>
    );
};

export interface IConnectedProps extends Pick<IWizardImportSubscribersLimitsReducer,
    'errorCode' |
    'importFileFormat' |
    'fileIsLoading' |
    'fileName' |
    'fileTemplateUrl' |
    'maxFileName' |
    'maxFileSize' |
    'maxImportSubscriberAmount' |
    'warningSettings'>
{
    wizardTranslation: IWizardImportSubscribersLimitsTranslation;
    commonTranslation: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    commonTranslation: makeSelectTranslationCommon(),
    errorCode: makeSelectWizardImportSubscribersLimitsAttachFileErrorCode(),
    importFileFormat: makeSelectWizardImportSubscribersLimitsImportFileFormat(),
    fileIsLoading: makeSelectWizardImportSubscribersLimitsFileIsLoading(),
    fileName: makeSelectWizardImportSubscribersLimitsFileName(),
    fileTemplateUrl: makeSelectWizardImportSubscribersLimitsFileTemplateUrl(),
    maxImportSubscriberAmount: makeSelectWizardImportSubscribersLimitsMaxImportSubscriberAmount(),
    maxFileName: makeSelectWizardImportSubscribersLimitsMaxFileName(),
    maxFileSize: makeSelectWizardImportSubscribersLimitsMaxFileSize(),
    warningSettings: makeSelectWizardImportSubscribersLimitsWarningSettings(),
    wizardTranslation: makeSelectTranslationWizardImportSubscribersLimits(),
});

export interface IDispatchProps {
    attachFile: (payload: IWizardImportSubscribersLimitsAttachFileActionPayload) => void;
    setStepValid: (payload: IWizardImportSubscribersLimitsSetStepValidPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    attachFile: (payload: IWizardImportSubscribersLimitsAttachFileActionPayload) =>
        dispatch(wizardImportSubscribersLimitsAttachFile(payload)),
    setStepValid: (payload: IWizardImportSubscribersLimitsSetStepValidPayload) =>
        dispatch(wizardImportSubscribersLimitsSetStepValidAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardImportSubscribersLimitsStepSelection);
